import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import ChoiceRevealOnScroll from "../shared/ChoiceRevealOnScroll.jsx";
import { fadeUpVariants, staggerContainer } from "../shared/animations.js";

const DeploymentPlugIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22v-5" />
    <path d="M9 8V2" />
    <path d="M15 8V2" />
    <path d="M18 8v5a6 6 0 0 1-12 0V8z" />
  </svg>
);

const DeploymentAutonomyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
  </svg>
);

const DeploymentSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const pillars = [
    {
      tag: "INTEGRATION",
      title: "Vibe-Native Integration",
      description:
        "The digital employee seamlessly integrates into your existing systems within 72 hours. It qualifies, logs, and books—delivering calendar-ready meetings directly to your team's current workspace.",
      icon: <DeploymentPlugIcon />,
    },
    {
      tag: "DAY ONE",
      title: "Zero Training Required",
      description:
        "There are no software training sessions or learning curves for your staff. The digital employee operates autonomously in the background from day one—your people close; it handles the front of the funnel.",
      icon: <DeploymentAutonomyIcon />,
    },
  ];

  return (
    <section className="section-padding mesh-gradient relative overflow-hidden" data-testid="deployment-section" ref={ref}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(251,146,60,0.07),transparent_60%)] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
        <ChoiceRevealOnScroll>
          <div className="bento-card relative overflow-hidden border border-orange-500/25 bg-gradient-to-b from-orange-500/[0.07] via-white/[0.02] to-transparent p-6 sm:p-10 lg:p-12">
            <div className="absolute -top-20 -right-20 w-56 h-56 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 lg:gap-12 pb-8 sm:pb-10 mb-8 sm:mb-10 border-b border-white/[0.08]">
              <div className="flex-1 min-w-0">
                <span className="badge-mono text-orange-400 bg-orange-500/10 border border-orange-500/20 px-3 py-1.5 rounded-full inline-block mb-5 sm:mb-6">
                  THE DEPLOYMENT
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-[2.75rem] font-bold mb-4 sm:mb-5 leading-tight" data-testid="deployment-headline">
                  Zero Entry Barrier.
                  <br />
                  <span className="gradient-text">72-Hour Launch.</span>
                </h2>
                <p className="text-base sm:text-lg text-gray-400 font-body leading-relaxed max-w-xl" data-testid="deployment-intro">
                  Launching a <span className="text-orange-400 font-semibold">digital employee</span> should not require an IT department or a change in your team&apos;s daily habits. Live in{" "}
                  <span className="text-orange-400 font-semibold">72 hours</span>—{" "}
                  <span className="text-orange-400 font-semibold">9-second</span> replies and qualified, calendar-booked meetings from day one on the path to{" "}
                  <span className="text-orange-400 font-semibold">2x more qualified appointments</span>.
                </p>
              </div>

              <div className="flex-shrink-0 flex flex-col items-center lg:items-end">
                <div className="relative">
                  <span className="text-[5rem] sm:text-[6.5rem] font-bold font-mono text-orange-400/90 leading-none tracking-tighter">72</span>
                  <span className="absolute -top-1 -right-3 sm:-right-4 text-orange-400/60 font-mono text-lg sm:text-xl">h</span>
                </div>
                <span className="text-[10px] sm:text-xs font-mono text-gray-500 tracking-[0.25em] mt-2">LAUNCH WINDOW</span>
                <div className="w-full min-w-[180px] sm:min-w-[220px] mt-4">
                  <div className="flex justify-between text-[10px] font-mono text-gray-600 mb-1.5">
                    <span>HOUR 0</span>
                    <span className="text-orange-400">LIVE</span>
                  </div>
                  <div className="h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-orange-600 via-orange-400 to-amber-300 rounded-full"
                      initial={{ width: "0%" }}
                      animate={isInView ? { width: "100%" } : { width: "0%" }}
                      transition={{ duration: 1.4, ease: [0.4, 0, 0.2, 1], delay: 0.3 }}
                    />
                  </div>
                </div>
                <p
                  className="text-[10px] sm:text-xs text-gray-500 font-mono text-center max-w-[220px] mt-3 leading-relaxed"
                  data-testid="deployment-launch-note"
                >
                  Standard Plug&amp;Play setup. Custom CRM or legacy integrations scoped separately at kickoff.
                </p>
              </div>
            </div>

            <motion.div
              className="relative grid sm:grid-cols-2 gap-6 sm:gap-0"
              variants={staggerContainer}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {pillars.map((pillar, index) => (
                <motion.div
                  key={pillar.title}
                  className={`relative px-0 sm:px-8 ${index === 0 ? "sm:pr-10 sm:border-r border-white/[0.08]" : "sm:pl-10 pt-6 sm:pt-0 border-t sm:border-t-0 border-white/[0.08]"}`}
                  variants={fadeUpVariants}
                  data-testid={`deployment-pillar-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/25 flex items-center justify-center text-orange-400">
                      {pillar.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="badge-mono text-orange-400/70 text-[10px] tracking-widest mb-2 block">{pillar.tag}</span>
                      <h3 className="text-lg sm:text-xl font-bold mb-2 text-white">{pillar.title}</h3>
                      <p className="text-gray-400 font-body leading-relaxed text-sm sm:text-base">{pillar.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </ChoiceRevealOnScroll>
      </div>
    </section>
  );
};

export default DeploymentSection;
